import React, { useMemo } from "react";

const CHART = {
  size: 420,
  maxRadius: 150,
  labelRadius: 182,
};

const toPoint = (center, radius, angle) => ({
  x: center + radius * Math.cos(angle),
  y: center + radius * Math.sin(angle),
});

export default function CognitiveRadarChart({ profile }) {
  const center = CHART.size / 2;
  const axes = profile || [];
  const count = axes.length || 1;
  const angleStep = (2 * Math.PI) / count;
  const startAngle = -Math.PI / 2;

  const axisAngles = useMemo(
    () => axes.map((_, index) => startAngle + index * angleStep),
    [axes, angleStep, startAngle]
  );

  const valuePoints = useMemo(
    () =>
      axes.map((axis, index) => {
        const value = axis.hasData ? axis.percentage : 0;
        const radius = (value / 100) * CHART.maxRadius;
        return toPoint(center, radius, axisAngles[index]);
      }),
    [axes, axisAngles, center]
  );

  const polygonPoints = valuePoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  const gridLevels = [25, 50, 75, 100];

  return (
    <div className="expedientes-radar-wrap">
      <svg
        viewBox={`0 0 ${CHART.size} ${CHART.size}`}
        className="expedientes-radar-chart"
        role="img"
        aria-label="Radar de perfil cognitivo-emocional integrado"
      >
        {gridLevels.map((level) => {
          const radius = (level / 100) * CHART.maxRadius;
          const points = axes
            .map((_, index) => {
              const point = toPoint(center, radius, axisAngles[index]);
              return `${point.x},${point.y}`;
            })
            .join(" ");

          return (
            <polygon
              key={level}
              points={points}
              className="expedientes-radar-grid"
            />
          );
        })}

        {axisAngles.map((angle, index) => {
          const outer = toPoint(center, CHART.maxRadius, angle);
          return (
            <line
              key={`axis-${axes[index]?.id || index}`}
              x1={center}
              y1={center}
              x2={outer.x}
              y2={outer.y}
              className="expedientes-radar-axis"
            />
          );
        })}

        <polygon
          points={polygonPoints}
          className="expedientes-radar-area"
        />
        <polygon
          points={polygonPoints}
          className="expedientes-radar-line"
        />

        {valuePoints.map((point, index) => (
          <circle
            key={`point-${axes[index].id}`}
            cx={point.x}
            cy={point.y}
            r={axes[index].hasData ? 4.5 : 3}
            className={
              axes[index].hasData
                ? "expedientes-radar-point"
                : "expedientes-radar-point expedientes-radar-point--empty"
            }
          >
            <title>
              {`${axes[index].label}: ${
                axes[index].hasData ? `${axes[index].percentage}%` : "Sin datos"
              }`}
            </title>
          </circle>
        ))}

        {axisAngles.map((angle, index) => {
          const labelPoint = toPoint(center, CHART.labelRadius, angle);
          return (
            <text
              key={`label-${axes[index].id}`}
              x={labelPoint.x}
              y={labelPoint.y}
              className="expedientes-radar-label"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {axes[index].label}
            </text>
          );
        })}
      </svg>

      <ul className="expedientes-radar-legend">
        {axes.map((axis) => (
          <li key={axis.id} className="expedientes-radar-legend-item">
            <span className="expedientes-radar-legend-name">{axis.label}</span>
            <span className="expedientes-radar-legend-value">
              {axis.hasData ? `${axis.percentage}%` : "Sin datos"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
